/**
 * GlassSurface — the foundational floating panel.
 *
 * Wraps any content in an iOS-26-style Liquid Glass material:
 *   - backdrop-filter blur + saturate (real refraction)
 *   - layered fill, stroke, shadow, and inner glow
 *   - optional reactive specular sheen that follows the cursor
 *
 * The cursor-tracking sheen is a subtle radial light that brightens
 * the area under the user's pointer. On touch devices we render a
 * static diagonal sheen instead so the surface still has highlight
 * even without a cursor.
 *
 * Three intensity variants:
 *   - 'soft'    — light blur, see-through
 *   - 'regular' — default workhorse
 *   - 'strong'  — opaque-ish, used for sheets/modals
 */
import { type CSSProperties, type HTMLAttributes, useRef, useEffect } from 'react';
import { cn } from '@/lib/cn';

type Variant = 'soft' | 'regular' | 'strong';

interface GlassSurfaceProps extends HTMLAttributes<HTMLDivElement> {
  variant?: Variant;
  /** Render a moving radial highlight that follows the cursor (desktop only) */
  reactive?: boolean;
  /** Add a coloured outer glow ring (for active/highlighted state) */
  glow?: boolean | 'lunar' | 'plasma' | 'nebula';
  /** Override border radius — defaults to var(--radius-card) */
  rounded?: 'card' | 'pill' | 'sheet' | 'none';
  /** Enable backdrop-filter blur — performance-expensive, opt-in only */
  blur?: boolean;
}

const variantClass: Record<Variant, string> = {
  soft: 'lg-surface',
  regular: 'lg-surface',
  strong: 'lg-surface lg-surface-strong lg-sheen',
};

const roundedClass: Record<NonNullable<GlassSurfaceProps['rounded']>, string> = {
  card: 'rounded-[var(--radius-card)]',
  pill: 'rounded-[var(--radius-pill)]',
  sheet: 'rounded-[var(--radius-sheet)]',
  none: 'rounded-none',
};

const glowColor: Record<string, string> = {
  lunar: 'var(--accent-primary)',
  plasma: 'var(--accent-secondary)',
  nebula: 'var(--accent-tertiary)',
};

export function GlassSurface({
  variant = 'regular',
  reactive = false,
  glow = false,
  rounded = 'card',
  blur = false,
  className,
  style,
  children,
  ...rest
}: GlassSurfaceProps) {
  const ref = useRef<HTMLDivElement>(null);

  // Reactive cursor sheen — DESKTOP ONLY. The CSS media query
  // (hover: hover) and (pointer: fine) hides the effect on touch
  // devices, but we also short-circuit the JS listener so we never
  // fire pointermove events on phones (which would still cost CPU).
  useEffect(() => {
    if (!reactive) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    const el = ref.current;
    if (!el) return;
    const onMove = (e: PointerEvent) => {
      const rect = el.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      el.style.setProperty('--lg-x', `${x}%`);
      el.style.setProperty('--lg-y', `${y}%`);
    };
    el.addEventListener('pointermove', onMove);
    return () => el.removeEventListener('pointermove', onMove);
  }, [reactive]);

  const glowStyle: CSSProperties = glow
    ? (() => {
        const colorKey: 'lunar' | 'plasma' | 'nebula' =
          glow === true ? 'lunar' : glow;
        const c = glowColor[colorKey];
        return {
          boxShadow: `
            var(--glass-shadow),
            var(--glass-inner-glow),
            0 0 0 1px ${c},
            0 0 48px -8px color-mix(in oklab, ${c} 60%, transparent)
          `,
        };
      })()
    : {};

  return (
    <div
      ref={ref}
      className={cn(
        variantClass[variant],
        roundedClass[rounded],
        'relative overflow-hidden',
        blur && 'has-warp-layer',
        reactive && 'lg-reactive',
        className,
      )}
      style={{ ...glowStyle, ...style }}
      {...rest}
    >
      {/* Refraction layer — only renders when blur is enabled. The
          empty div has backdrop-filter blur AND filter:url(#lg-glass-warp)
          which physically warps the rendered backdrop into a real
          glass lens (iPhone Liquid Glass effect). */}
      {blur && <div className="lg-glass-warp-layer" aria-hidden />}
      <div className="relative z-10">{children}</div>
    </div>
  );
}
